import { Button, Tooltip, Typography } from 'antd';
import type { ColumnType, ColumnsType } from 'antd/es/table';
import { EyeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';

import Table from '../../../../components/general/table';
import { ExportExcelProps } from '../../../../helpers/excel';
import moment from 'moment';
import { ExportPdfProps } from '../../../../helpers/pdf';
import { OrderCustomer } from '../../../../api/models/orders';
import BackOrderForm from '../detail';
import { useEffect, useState } from 'react';
import { formatMoney } from '../../../../utils/general';
import { CanShow } from '../../../../components';
import { useUser } from '../../../../hooks';

const scrollTable = 2100;
const label = 'orden';

type GenerateTableColumnProps = {
  getColumnSearchProps: (propName: string) => ColumnType<OrderCustomer>;
  showModal: (type: string, id: number | null) => void;
  handleDelete: (id: number) => void;
};

const BackOrderCustomerList = (props: any) => {
  const { customerSelected, customerName } = props;
  const { Text } = Typography;
  const [route, setRoute] = useState('/orders');
  const [extendedRoute, setExtendedRoute] = useState(
    `/customers/${customerSelected}/orders`
  );
  const titlePdf = `Reporte de ordenes ${customerName} : ${customerSelected}`;
  const { user } = useUser();

  useEffect(() => {
    setExtendedRoute(`/customers/${customerSelected}/orders`);
  }, [customerSelected]);

  const generateTableColumns: (
    props: GenerateTableColumnProps
  ) => ColumnsType<OrderCustomer> = (props) => {
    const { getColumnSearchProps, showModal, handleDelete } = props;

    return [
      {
        title: 'F. Captura',
        dataIndex: 'orderHed_OrderDate',
        key: 'orderHed_OrderDate',
        ...getColumnSearchProps('orderHed_OrderDate'),
        render: (orderHed_OrderDate) => (
          <>{moment.tz(orderHed_OrderDate, 'UTC').format('DD/MM/YYYY')}</>
        ),
      },
      {
        title: 'No. Pedido',
        dataIndex: 'num_order',
        key: 'num_order',
        ...getColumnSearchProps('num_order'),
      },
      {
        title: 'No. Parte',
        key: 'num_part',
        dataIndex: 'num_part',
        ...getColumnSearchProps('num_part'),
      },
      {
        title: 'Descripción',
        dataIndex: 'description',
        key: 'description',
        ...getColumnSearchProps('description'),
      },
      {
        title: 'P. Unitario',
        dataIndex: 'unit_price',
        key: 'unit_price',
        ...getColumnSearchProps('unit_price'),
        render: (unit_price) => <Text>${formatMoney(unit_price)}</Text>,
      },
      {
        title: 'Cantidad',
        dataIndex: 'quantity',
        key: 'quantity',
        ...getColumnSearchProps('quantity'),
      },
      {
        title: 'Pdte. Embarque',
        dataIndex: 'pending_shipment',
        key: 'pending_shipment',
        ...getColumnSearchProps('pending_shipment'),
      },
      {
        title: 'Enviado A ',
        dataIndex: 'ship_to',
        key: 'ship_to',
        ...getColumnSearchProps('ship_to'),
      },
      {
        title: 'Warehouse',
        dataIndex: 'warehouse',
        key: 'warehouse',
        ...getColumnSearchProps('warehouse'),
      },
      {
        title: 'Acciones',
        key: 'sup',
        fixed: user?.role !== 'Customer' ? 'right' : undefined,
        width: 150,
        align: 'center',
        render: (order) => (
          <CanShow roles={['Admin', 'Financial']}>
            <>
              <Tooltip title="Ver más información">
                <Button
                  type="primary"
                  ghost
                  shape="default"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => showModal('watch', order.num_order)}
                />
              </Tooltip>
              <Tooltip title="Editar">
                <Button
                  style={{
                    marginLeft: 10,
                    backgroundColor: '#1b4289',
                    borderColor: '#1b4289',
                  }}
                  type="primary"
                  shape="default"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => showModal('edit', order.num_order)}
                />
              </Tooltip>
              <Tooltip title="Eliminar">
                <Button
                  style={{
                    marginLeft: 10,
                    backgroundColor: '#e63241',
                    borderColor: '#cf1322',
                  }}
                  type="primary"
                  shape="default"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(order.num_order)}
                />
              </Tooltip>
            </>
          </CanShow>
        ),
      },
    ];
  };

  const generateExportExcelProps: () => ExportExcelProps = () => ({
    fileName: 'Reporte_BackOrders',
    sheetName: 'BackOrders',
    tableName: `Tabla de orders ${customerName} : ${customerSelected}`,
    columnNames: [
      {
        label: 'F. Captura',
        key: 'orderHed_OrderDate',
        format: (val) => moment.tz(val, 'UTC').format('DD/MM/YYYY'),
      },
      { label: 'No. Pedido', key: 'num_order' },
      { label: 'No. Parte', key: 'num_part' },
      { label: 'Descripción', key: 'description' },
      { label: 'P. Unitario', key: 'unit_price' },
      { label: 'Cantidad', key: 'quantity' },
      { label: 'Pdte. Embarque', key: 'pending_shipment' },
      { label: 'Enviado A', key: 'ship_to' },
      { label: 'Warehouse', key: 'warehouse' },
    ],
  });

  const pdfColumns: ExportPdfProps[] = [
    {
      header: 'F. Captura',
      key: 'orderHed_OrderDate',
      format: (val) => moment.tz(val, 'UTC').format('DD/MM/YYYY'),
    },
    { header: 'No. Pedido', key: 'num_order' },
    { header: 'No. Parte', key: 'num_part' },
    { header: 'Descripción', key: 'description' },
    { header: 'P. Unitario', key: 'unit_price' },
    { header: 'Cantidad', key: 'quantity' },
    { header: 'Pdte. Embarque', key: 'pending_shipment' },
    { header: 'Enviado A', key: 'ship_to' },
    { header: 'Warehouse', key: 'warehouse' },
  ];

  const backordersTableProps = {
    label,
    route,
    extendedRoute,
    generateTableColumns,
    generateExportExcelProps,
    detailForm: BackOrderForm,
    pdfColumns,
    titlePdf,
    scrollTable,
  };
  const BackOrdersCustomerTable = Table<OrderCustomer>(backordersTableProps);

  return BackOrdersCustomerTable;
};

export default BackOrderCustomerList;
